import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { ModalReact, ModalType } from "common/components/modal";
import { SvgIconReact } from "common/components/svg-icon";
import { ButtonReact } from "common/components/button";
import { ButtonTheme, ButtonType } from "common/components/button/constants";
import { trackUpsellEvent, UpsellType } from "common/report/util/tracking-helpers";
import { getUpsellInfo } from "common/widgets/in-product-upsell-modal/util";
import { ModelStatus } from "common/util/model-resource/model-resource";
import { useCurrentProject } from "common/contexts/react/current-project-context";
import { useThemes } from "common/contexts/react/themes-context";
import styles from "./index.module.styl";
const MODAL_VERSION = `v2`;
const UPSELL_TYPE_TO_TEXT = {
  [UpsellType.FREE_TO_GROWTH]: {
    ctaText: `Upgrade Plan`,
    ctaUpgradeLink: `/pricing/plan-builder/`,
    ctaExitText: `Continue With Free Plan \u2192`
  },
  [UpsellType.GROWTH_TO_ENTERPRISE]: {
    ctaText: `Contact Sales`,
    ctaUpgradeLink: `/contact-us/sales/`,
    ctaExitText: `Continue With Growth Plan \u2192`
  },
  // Default unknown to growth values
  [UpsellType.UNKNOWN]: {
    ctaText: `Upgrade Plan`,
    ctaUpgradeLink: `/pricing/plan-builder/`,
    ctaExitText: `Continue With Free Plan \u2192`
  }
};
export function template({ modalTrigger, open, closeModal, metadata = {} }) {
  const currentProject = useCurrentProject();
  const [upsellInfo, setUpsellInfo] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const themesResource = useThemes();
  const isDarkMode = themesResource.isDarkMode();
  useEffect(() => {
    const model = currentProject == null ? void 0 : currentProject.get();
    if ((model == null ? void 0 : model.status) === ModelStatus.Success && model.value) {
      const tmpInfo = getUpsellInfo(model.value, modalTrigger, metadata);
      if (tmpInfo) {
        setUpsellInfo(tmpInfo);
        setOrgId(model.value.organizationId);
        if (open) {
          trackUpsellEvent(`Upsell Modal Shown`, tmpInfo == null ? void 0 : tmpInfo.trackingFeatureGate, {
            version: MODAL_VERSION,
            upsellType: tmpInfo == null ? void 0 : tmpInfo.upsellType
          });
          trackUpsellEvent(`View Upsell`, tmpInfo == null ? void 0 : tmpInfo.trackingFeatureGate, {
            version: MODAL_VERSION,
            upsellType: tmpInfo == null ? void 0 : tmpInfo.upsellType
          });
        }
      }
    }
  }, [open, currentProject]);
  const handleClickUpgrade = () => {
    if (!upsellInfo) {
      return;
    }
    trackUpsellEvent(`Click Upsell`, upsellInfo.trackingFeatureGate, {
      version: MODAL_VERSION,
      upsellType: upsellInfo.upsellType
    });
  };
  const handleClose = () => {
    if (upsellInfo) {
      trackUpsellEvent(`Dismiss Upsell`, upsellInfo.trackingFeatureGate, {
        version: MODAL_VERSION,
        upsellType: upsellInfo.upsellType
      });
    }
    closeModal();
  };
  const GrowthGridItems = () => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: styles.gridItem }, /* @__PURE__ */ React.createElement(SvgIconReact, { icon: `brand-bar-chart` }), "Unlimited reports & cohorts"), /* @__PURE__ */ React.createElement("div", { className: styles.gridItem }, /* @__PURE__ */ React.createElement(SvgIconReact, { icon: `brand-gem` }), "Increased Spark AI report limits"), /* @__PURE__ */ React.createElement("div", { className: styles.gridItem }, /* @__PURE__ */ React.createElement(SvgIconReact, { icon: `brand-lock` }), "Advanced sharing controls"), /* @__PURE__ */ React.createElement("div", { className: styles.gridItem }, /* @__PURE__ */ React.createElement(SvgIconReact, { icon: `brand-laptop` }), "Real-time email & Slack alerts"));
  const EnterpriseGridItems = () => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: styles.gridItem }, /* @__PURE__ */ React.createElement(SvgIconReact, { icon: `brand-eye` }), "Higher session replay volumes"), /* @__PURE__ */ React.createElement("div", { className: styles.gridItem }, /* @__PURE__ */ React.createElement(SvgIconReact, { icon: `brand-clipboard` }), "Data governance controls"), /* @__PURE__ */ React.createElement("div", { className: styles.gridItem }, /* @__PURE__ */ React.createElement(SvgIconReact, { icon: `brand-plug` }), "Data warehouse ingestion"), /* @__PURE__ */ React.createElement("div", { className: styles.gridItem }, /* @__PURE__ */ React.createElement(SvgIconReact, { icon: `brand-hand-holding-heart` }), "Premium support & services"));
  return upsellInfo ? /* @__PURE__ */ React.createElement(
    ModalReact,
    {
      className: styles.modalContainer,
      open,
      modalTitle: upsellInfo == null ? void 0 : upsellInfo.title,
      modalDescription: upsellInfo == null ? void 0 : upsellInfo.description,
      modalType: ModalType.Modal
    },
    /* @__PURE__ */ React.createElement("div", { className: styles.modalBody }, /* @__PURE__ */ React.createElement("p", { className: styles.modalTitle }, upsellInfo == null ? void 0 : upsellInfo.title), /* @__PURE__ */ React.createElement("p", { className: styles.modalDescription }, upsellInfo == null ? void 0 : upsellInfo.description), /* @__PURE__ */ React.createElement("div", { className: clsx(styles.gridContainer, styles.spacedTop) }, upsellInfo.upsellType === UpsellType.GROWTH_TO_ENTERPRISE ? /* @__PURE__ */ React.createElement(EnterpriseGridItems, null) : /* @__PURE__ */ React.createElement(GrowthGridItems, null)), /* @__PURE__ */ React.createElement("div", { className: styles.spacedTop }, /* @__PURE__ */ React.createElement(
      ButtonReact,
      {
        className: clsx(styles.ctaButton, { [styles.darkMode]: isDarkMode }),
        theme: ButtonTheme.Tertiary,
        type: ButtonType.Info,
        href: upsellInfo ? `${UPSELL_TYPE_TO_TEXT[upsellInfo.upsellType].ctaUpgradeLink}?org=${orgId}` : ``,
        notFocusable: true,
        onClick: handleClickUpgrade
      },
      /* @__PURE__ */ React.createElement("div", { className: styles.ctaText }, upsellInfo ? UPSELL_TYPE_TO_TEXT[upsellInfo.upsellType].ctaText : ``)
    ), /* @__PURE__ */ React.createElement(
      ButtonReact,
      {
        className: clsx(styles.ctaTextButton, { [styles.darkMode]: isDarkMode }),
        theme: ButtonTheme.Tertiary,
        notFocusable: true,
        onClick: handleClose
      },
      /* @__PURE__ */ React.createElement("div", null, upsellInfo ? UPSELL_TYPE_TO_TEXT[upsellInfo.upsellType].ctaExitText : ``)
    )))
  ) : null;
}
